import type { ElementType } from 'react';

import { Box, Paper, Typography, styled } from '@mui/material';

import { WEEKDAYS } from '../../OpeningHours.constants';

/** A title row plus a row per weekday. */
const ROWS_PER_TILE = WEEKDAYS.length + 1;

/** Weekday, what applies, where it comes from: the same shares in every tile. */
const COLUMNS = '1fr 1.6fr 1.4fr';

/**
 * Tiles of equal width whose rows line up: each tile spans the same number of rows of this grid and
 * shares them through subgrid, so a row is as high as the highest cell in it across all tiles.
 */
export const StyledOverview = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  columnGap: theme.spacing(2),
  rowGap: 0
}));

export const StyledTile = styled(Paper)(({ theme }) => ({
  display: 'grid',
  gridRow: `span ${ROWS_PER_TILE}`,
  gridTemplateRows: 'subgrid',
  marginBottom: theme.spacing(2),
  overflow: 'hidden'
}));

export const StyledTileTitle = styled(Typography)<{ component?: ElementType }>(
  ({ theme }) => ({
    padding: theme.spacing(1.5)
  })
);

export const StyledRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: COLUMNS,
  columnGap: theme.spacing(1),
  alignItems: 'center',
  padding: theme.spacing(1, 1.5),
  borderTop: `1px solid ${theme.palette.divider}`
}));

/**
 * "Closed (no opening hours)" with its info icon; the whole of it opens the tooltip. The icon is
 * smaller than the text, so it reads as a hint rather than as part of the answer.
 */
export const StyledNoOpeningHours = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '& svg': {
    fontSize: theme.typography.pxToRem(12)
  }
}));

/** An answer that is not the plan unit's own is shown muted, so its own lines stand out. */
export const StyledCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'muted'
})<{ muted: boolean }>(({ theme, muted }) => ({
  ...theme.typography.body2,
  overflowWrap: 'anywhere',
  ...(muted && { color: theme.palette.text.secondary, fontStyle: 'italic' })
}));
